import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpContext} from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { User } from '../models/user';
import { AppConfig } from '../app.config';
import { ChangePasswordRequest } from '../requests/changePasswordRequest';
import { ResetPasswordTokenRequest } from '../requests/resetPasswordTokenRequest';
import { ResetPasswordRequest } from '../requests/resetPasswordRequest';
import { LoginRequest } from '../requests/loginRequest';
import { CreateUserRequest } from '../requests/createUserRequest';
import { UpdateUserRequest } from '../requests/updateUserRequest';
import { BasicResponse } from '../responses/basicResponse';
import { Customer } from '../models/customer';
import { RegisterCustomerRequest } from '../requests/registerCustomerRequest';
import { CreateCustomerRequest } from '../requests/createCustomerRequest';
import { UpdateCustomerRequest } from '../requests/updateCustomerRequest';
import { CreateClientRequest } from '../requests/createClientRequest';
import { Client } from '../models/client';
import { UpdateClientRequest } from '../requests/updateClientRequest';
import { LockUserRequest } from '../requests/lockUserRequest';
import { UnlockUserRequest } from '../requests/unlockUserRequest';
import { ResetPasswordVerificationRequest } from '../requests/resetPasswordVerificationRequest';
import { DeleteUserRequest } from '../requests/deleteUserRequest';
import { DeleteCustomerRequest } from '../requests/deleteCustomerRequest';
import { AdminChangePasswordRequest } from '../requests/adminChangePasswordRequest';
import { UndeleteUserRequest } from '../requests/undeleteUserRequest';
import { UndeleteCustomerRequest } from '../requests/undeleteCustomerRequest';
import { SFTPUser } from '../models/sftpUser';
import { CreateSFTPUserRequest } from '../requests/createSFTPUserRequest';
import { UpdateSFTPUserRequest } from '../requests/updateSFTPUserRequest';
import { SFTPUserChangePasswordRequest } from '../requests/sftpUserChangePasswordRequest';
import { EnableSFTPUserRequest } from '../requests/enableSFTPUserRequest';
import { CreateBrandingRequest } from '../requests/createBrandingRequest';
import { CustomerBranding } from '../models/customerBranding';
import { UpdateBrandingRequest } from '../requests/updateBrandingRequest';
import { DeleteBrandingRequest } from '../requests/deleteBrandingRequest';
import { Tokens } from '../models/tokens';
import { PremierServiceProduct } from '../models/premierServiceProduct';
import { CreatePremierServiceProductRequest } from '../requests/createPremierServiceProductRequest';
import { UpdatePremierServiceProductRequest } from '../requests/updatePremierServiceProductRequest';
import { EnablePremierServiceProductRequest } from '../requests/enablePremierServiceProductRequest';
import { CustomerPremierPartnerServiceConfiguration } from '../models/customerPremierPartnerServiceConfiguration';
import { CreateCustomerPremierPartnerServiceConfigurationRequest } from '../requests/createCustomerPremierPartnerServiceConfigurationRequest';
import { DeleteCustomerPremierPartnerServiceConfigurationRequest } from '../requests/deleteCustomerPremierPartnerServiceConfigurationRequest';
import { UpdateCustomerPremierPartnerServiceConfigurationRequest } from '../requests/updateCustomerPremierPartnerServiceConfigurationRequest';
import { QBProcessAuthorizationCodeRequest } from '../requests/qbProcessAuthorisationCodeRequest';
import { AuthorizedSenderEmailAddress } from '../models/authorizedSenderEmailAddress';
import { CreateAuthorizedSenderEmailAddressRequest } from '../requests/createAuthorizedSenderEmailAddressRequest';
import { UpdateAuthorizedSenderEmailAddressRequest } from '../requests/updateAuthorizedSenderEmailAddressRequest';
import { EnableAuthorizedSenderEmailAddressRequest } from '../requests/enableAuthorizedSenderEmailAddressRequest';
import { CustomerSecurityCompliance } from '../models/customerSecurityCompliance';
import { UpdateCustomerSecurityComplianceRequest } from '../requests/updateCustomerSecurityComplianceRequest';
import { QBIsEntityAuthorizedRequest } from '../requests/qbIsEntityAuthorizedRequest';
import { CustomerFeeSchedule } from '../models/customerFeeSchedule';
import { UpdateCustomerFeeScheduleRequest } from '../requests/updateCustomerFeeScheduleRequest';
import { XeroIsEntityAuthorizedRequest } from '../requests/xeroIsEntityAuthorizedRequest';
import { XeroProcessAuthorizationCodeRequest } from '../requests/xeroProcessAuthorisationCodeRequest';
import { DropboxProcessAuthorizationCodeRequest } from '../requests/dropboxProcessAuthorisationCodeRequest';
import { DropboxIsEntityAuthorizedRequest } from '../requests/dropboxIsEntityAuthorizedRequest';
import { FileSharingAccount } from '../models/fileSharingAccount';
import { UpdateFileSharingAccountRequest } from '../requests/updateFileSharingAccountRequest';
import { DeleteFileSharingAccountRequest } from '../requests/deleteFileSharingAccountRequest';
import { DropboxRevokeTokenRequest } from '../requests/dropboxRevokeTokenRequest';
import { QBEntityDeleteRequest } from '../requests/qbEntityDeleteRequest';
import { XeroEntityDeleteRequest } from '../requests/xeroEntityDeleteRequest';
import { MicrosoftProcessAdminConsentRequest } from '../requests/microsoftProcessAdminConsentRequest';
import { MicrosoftAppType } from '../models/moovStepDefinition';
import { MicrosoftIsEntityAuthorizedRequest } from '../requests/microsoftIsEntityAuthorizedRequest';
import { MicrosoftAuthorizedEntitiesRequest } from '../requests/microsoftAuthorizedEntitiesRequest';
import { MicrosoftEntityDisconnectedRequest } from '../requests/microsoftEntityDisconnectedRequest';
import { MicrosoftEntityDeleteRequest } from '../requests/microsoftEntityDeleteRequest';
import { Vendor } from '../models/vendor';
import { CreateVendorRequest } from '../requests/createVendorRequest';
import { UpdateVendorRequest } from '../requests/updateVendorRequest';
import { DeleteVendorRequest } from '../requests/deleteVendorRequest';
import { UndeleteVendorRequest } from '../requests/undeleteVendorRequest';
import { BillingBucket } from '../models/billingBucket';
import { CreateBillingBucketRequest } from '../requests/createBillingBucketRequest';
import { UpdateBillingBucketRequest } from '../requests/updateBillingBucketRequest';
import { EnableBillingBucketRequest } from '../requests/enableBillingBucketRequest';
import { CustomerAbstractsRequest } from '../requests/customerAbstractsRequest';
import { LoggingService } from './LoggingService';
import { PrismHRAPIPermissionsRequest } from '../requests/prismHRAPIPermissionsRequest';
import { WizardSession } from '../models/wizardSession';
import { SKIP_AUTH_INTERCEPTOR } from '../theme/utils/http-context-tokens';
import { PremierServiceProductsReponse } from '../responses/premierServiceProductsResponse';
import { CustomerAbstractDTO } from '../DTO/customerAbstractDTO';

@Injectable({
  providedIn: 'root'
})
export class UserManagementApiService {
  
  constructor(private httpClient: HttpClient, private appConfig:AppConfig, private logger:LoggingService) { 
  }

  get serviceURL():string {
    return this.appConfig.getConfig("usermanagementapi_external_url") + '/api/';
  }
  get siteURL(): string {
    return this.appConfig.getConfig('website_url');
  }
  get optionsJson(){
    return this.appConfig.optionsJson;
  }
  get optionsForm(){
    return this.appConfig.optionsForm;
  }

  //#region user authentication
  public refreshTokens(accessToken:string, refreshToken:string):Observable<any>{
    let tokens=new Tokens();
    tokens.accessToken=accessToken;
    tokens.refreshToken=refreshToken;
    return this.httpClient.post<any>(this.serviceURL+`refresh`, tokens, this.optionsJson).pipe(catchError((error: any):any => this.handleError(error)));
  }
  
  public verifyRegistrationToken(token:string):Observable<any>{
    this.logger.log("Verifying registration: " + token);
    return this.httpClient.post<any>(this.serviceURL+`verifyRegistration/`+ token, { headers: new HttpHeaders().set('Content-Type', 'application/json') }).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public loginUser(email:string, password:string):Observable<any>{
    let r = new LoginRequest();
    r.email = email;
    r.password = password;
    r.refreshToken = this.appConfig.refreshToken ?? ''; //i need this as the local storage could already have a refresh token from a previous session. so the server will verify if it is still valid and reuse or not.
    return this.httpClient.post<any>(this.serviceURL+`login`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public setupTwoFactor(userId:number){
    this.logger.log("Setting up 2FA");
    return this.httpClient.get<any>(this.serviceURL+`setupTwoFactor/`+ userId, this.optionsJson).pipe(catchError((error: any):any => this.handleError(error)));
  }

  public validateTwoFactor(userId:number,validationKey:string){
    this.logger.log("Validating 2FA");
    return this.httpClient.get<any>(this.serviceURL+`validateTwoFactor/`+ userId + '/' + validationKey, this.optionsJson).pipe(catchError((error: any):any => this.handleError(error)));
  }

  public removeTwoFactor(userId:number){
    this.logger.log("Removing 2FA");
    return this.httpClient.get<any>(this.serviceURL+`removeTwoFactor/`+ userId, this.optionsJson).pipe(catchError((error: any):any => this.handleError(error)));
  }

  public recoverTwoFactor(userId:number){
    this.logger.log("Recovering 2FA");
    return this.httpClient.get<any>(this.serviceURL+`recoverTwoFactor/`+ userId, this.optionsJson).pipe(catchError((error: any):any => this.handleError(error)));
  }

  public validateTwoFactorRecovery(userId:number,recoveryToken:string){
    this.logger.log("Validating 2FA Recovery");
    return this.httpClient.get<any>(this.serviceURL+`validateTwoFactorRecovery/`+ userId + '/' + recoveryToken, this.optionsJson).pipe(catchError((error: any):any => this.handleError(error)));
  }
  //#endregion

  //#region customer
  public registerCustomer(c:Customer, u:User, password:string):Observable<any>{
    let r = new RegisterCustomerRequest();
    r.customer = Customer.mapToDTO(c);
    r.user = User.mapToDTO(u);
    r.password = password;
    return this.httpClient.post<any>(this.serviceURL+`registerCustomer`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public createCustomer(c:Customer, u:User):Observable<any>{
    let r = new CreateCustomerRequest();
    r.customer = Customer.mapToDTO(c);
    r.user = User.mapToDTO(u);
    return this.httpClient.post<any>(this.serviceURL+`createCustomer`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public updateCustomer(c:Customer):Observable<any>{
    let r = new UpdateCustomerRequest();
    r.customer = Customer.mapToDTO(c);
    return this.httpClient.put<any>(this.serviceURL+`updateCustomer`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public deleteCustomer(id:number, physicalDelete:boolean):Observable<any>{
    let r = new DeleteCustomerRequest();
    r.customerId = id;
    r.physicalDelete = physicalDelete;
    //at the time of coding this httpClient.delete does not allow for a body. using universal request instead.
    return this.httpClient.request<any>('delete', this.serviceURL+`deleteCustomer`, {body: r, headers: this.optionsJson.headers}).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public undeleteCustomer(id:number):Observable<any>{
    let r = new UndeleteCustomerRequest();
    r.customerId = id;
    //at the time of coding this httpClient.delete does not allow for a body. using universal request instead.
    return this.httpClient.put<any>(this.serviceURL+`undeleteCustomer`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getCustomers(){
    this.logger.log("Getting customers");
    return this.httpClient.get<any>(this.serviceURL+`customers`, this.optionsJson).pipe(catchError((error: any):any => this.handleError(error)));
  }

  public getCustomerAbstractsForClientIds(entityId:number|undefined, entityType:string|undefined, clientIdList:number[]){
    this.logger.log("Getting customer abstracts");
    let r= new CustomerAbstractsRequest();
    r.requestorEntityId = entityId;
    r.requestorEntityType = entityType;
    r.clientIdList = clientIdList;
    return this.httpClient.post<any>(this.serviceURL+`customerAbstractsForClientIds`, r, this.optionsJson).pipe(catchError((error: any):any => this.handleError(error)));
  }
  //#endregion

  //#region vendor
  public createVendor(v:Vendor, u:User):Observable<any>{
    let r = new CreateVendorRequest();
    r.vendor = Vendor.mapToDTO(v);
    r.user = User.mapToDTO(u);
    return this.httpClient.post<any>(this.serviceURL+`createVendor`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public updateVendor(v:Vendor):Observable<any>{
    let r = new UpdateVendorRequest();
    r.vendor = Vendor.mapToDTO(v);
    return this.httpClient.put<any>(this.serviceURL+`updateVendor`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public deleteVendor(id:number, physicalDelete:boolean):Observable<any>{
    let r = new DeleteVendorRequest();
    r.vendorId = id;
    r.physicalDelete = physicalDelete;
    //at the time of coding this httpClient.delete does not allow for a body. using universal request instead.
    return this.httpClient.request<any>('delete', this.serviceURL+`deleteVendor`, {body: r, headers: this.optionsJson.headers}).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public undeleteVendor(id:number):Observable<any>{
    let r = new UndeleteVendorRequest();
    r.vendorId = id;
    //at the time of coding this httpClient.delete does not allow for a body. using universal request instead.
    return this.httpClient.put<any>(this.serviceURL+`undeleteVendor`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getVendors(){
    this.logger.log("Getting vendors");
    return this.httpClient.get<any>(this.serviceURL+`vendors`, this.optionsJson).pipe(catchError((error: any):any => this.handleError(error)));
  }
  //#endregion

  //#region client
  public createClient(c:Client):Observable<any>{
    let r = new CreateClientRequest();
    r.client = Client.mapToDTO(c);
    r.customerId = c.customerId;
    return this.httpClient.post<any>(this.serviceURL+`createClient`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public updateClient(c:Client):Observable<any>{
    let r = new UpdateClientRequest();
    r.client = Client.mapToDTO(c);
    return this.httpClient.put<any>(this.serviceURL+`updateClient`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getCustomerClients(customerId:number){
    this.logger.log("Getting customer clients");
    return this.httpClient.get<any>(this.serviceURL+`customerClients/` + customerId, this.optionsJson).pipe(catchError((error: any):any => this.handleError(error)));
  }
  //#endregion

  //#region user & password handling
  public createUser(u:User, entityId:number, entityType:string):Observable<any>{
    let dto=User.mapToDTO(u);
    let r=new CreateUserRequest();
    r.requestorEntityId = entityId;
    r.requestorEntityType = entityType;
    r.user=dto;
    return this.httpClient.post<any>(this.serviceURL+`createUser`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public updateUser(u:User):Observable<any>{
    let dto=User.mapToDTO(u);
    let r=new UpdateUserRequest();
    r.user=dto;
    return this.httpClient.put<any>(this.serviceURL+`updateUser`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public changePassword(currentPassword:string, newPassword:string):Observable<any>{
    let r=new ChangePasswordRequest();
    r.oldPwd=currentPassword;
    r.newPwd=newPassword;
    return this.httpClient.put<any>(this.serviceURL+`changePwd`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public resetPasswordVerification(token: string):Observable<any>{
    let r=new ResetPasswordVerificationRequest();
    r.token=token;
    return this.httpClient.post<any>(this.serviceURL+`verifyPwdReset`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public resetPasswordToken(email: string):Observable<any>{
    let r=new ResetPasswordTokenRequest();
    r.email=email;
    return this.httpClient.post<any>(this.serviceURL+`requestPwdResetToken`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public resetPassword(token:string, newPassword:string):Observable<any>{
    let r=new ResetPasswordRequest();
    r.newPwd=newPassword;
    r.token=token;
    return this.httpClient.post<any>(this.serviceURL+`requestPwdReset`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public adminPwdChange(id: number, newPassword:string){
    let r=new AdminChangePasswordRequest();
    r.userId=id
    r.newPwd=newPassword;
    return this.httpClient.put<any>(this.serviceURL+`adminPwdChange`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  
  public getUserRoles(requestorId:number){
    this.logger.log("Getting user roles");
    return this.httpClient.get<any>(this.serviceURL+`userRoles/`+ requestorId, this.optionsJson).pipe(catchError((error: any):any => this.handleError(error)));
  }

  public getUsers(entityId:number, entityType:string){
    this.logger.log("Getting users");
    return this.httpClient.get<any>(this.serviceURL+`users/`+ (entityId?(entityId):'') + (entityType?('/' + entityType):''), this.optionsJson).pipe(catchError((error: any):any => this.handleError(error)));
  }

  public getAllUsers(activeOnly:boolean){
    this.logger.log("Getting all users");
    return this.httpClient.get<any>(this.serviceURL+`allUsers/` + activeOnly, this.optionsJson).pipe(catchError((error: any):any => this.handleError(error)));
  }

  public getUserName(userId:number){
    //to retrieve a users name in case a user is referenced outside the current users domain
    this.logger.log("Getting user name");
    return this.httpClient.get<any>(this.serviceURL+`userName/`+ userId, this.optionsJson).pipe(catchError((error: any):any => this.handleError(error)));
  }

  public lockUser(id:number):Observable<any>{
    let r=new LockUserRequest();
    r.userId=id;
    return this.httpClient.put<any>(this.serviceURL+`lockUser`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public unlockUser(id:number):Observable<any>{
    let r=new UnlockUserRequest();
    r.userId=id;
    return this.httpClient.put<any>(this.serviceURL+`unlockUser`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public deleteUser(id:number, physicalDelete:boolean):Observable<any>{
    let r=new DeleteUserRequest();
    r.userId=id;
    r.physicalDelete=physicalDelete;
    //at the time of coding this httpClient.delete does not allow for a body. using universal request instead.
    return this.httpClient.request<any>('delete', this.serviceURL+`deleteUser`, {body: r, headers: this.optionsJson.headers}).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public undeleteUser(id:number):Observable<any>{
    let r=new UndeleteUserRequest();
    r.userId=id;
    return this.httpClient.put<any>(this.serviceURL+`undeleteUser`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  //#endregion

  //#region sftp user
  public createSFTPUser(u:SFTPUser):Observable<any>{
    let dto=SFTPUser.mapToDTO(u);
    let r=new CreateSFTPUserRequest();
    r.sftpUser=dto;
    return this.httpClient.post<any>(this.serviceURL+`createSFTPUser`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public updateSFTPUser(u:SFTPUser):Observable<any>{
    let dto=SFTPUser.mapToDTO(u);
    let r=new UpdateSFTPUserRequest();
    r.sftpUser=dto;
    return this.httpClient.put<any>(this.serviceURL+`updateSFTPUser`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public changeSFTPUserPassword(id:number, newPassword:string):Observable<any>{
    let r=new SFTPUserChangePasswordRequest();
    r.sftpUserId=id;
    r.newPwd=newPassword;
    return this.httpClient.put<any>(this.serviceURL+`sftpUserPwdChange`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public enableSFTPUser(id:number, enable:boolean):Observable<any>{
    let r=new EnableSFTPUserRequest();
    r.sftpUserId=id;
    r.enable=enable;
    return this.httpClient.put<any>(this.serviceURL+`enableSFTPUser`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getSFTPUsers(customerId:number){
    this.logger.log("Getting sftp users");
    return this.httpClient.get<any>(this.serviceURL+`customerSFTPUsers/` + customerId, this.optionsJson).pipe(catchError((error: any):any => this.handleError(error)));
  }

  public getSFTPHost(){
    this.logger.log("Getting sftp host");
    return this.httpClient.get<any>(this.serviceURL+`sftpHost`, this.optionsJson).pipe(catchError((error: any):any => this.handleError(error)));
  }

  //#endregion
  
  //#region customer branding
  public createBranding(b:CustomerBranding, file:File){
    let r = new CreateBrandingRequest();
    r.customerBranding = CustomerBranding.mapToDTO(b);
    r.requestorEntityId=b.customerId;
    r.requestorEntityType="C";
    const formData: FormData = new FormData();
    let body = JSON.stringify(r);
    formData.append('requestString', body);
    if(file){
      let fileName = b.brandName + '.png';
      formData.append('files', file, fileName);
    }
    this.logger.log("Uploading Brand file");
    return this.httpClient.post<any>(this.serviceURL + `createBranding`, formData, this.optionsForm).pipe(catchError((error: any): any => { this.handleError(error); return of(this.createBasicResponseError(error))}));
  }

  public getCustomerBrandings(customerId:number){
    this.logger.log("Getting Brandings");
    return this.httpClient.get<any>(this.serviceURL+ `customerBrandings/` + customerId, this.optionsJson).pipe(catchError((error: any): any=>this.handleError(error))); 
  }

  public downloadData(url:string){
    return this.httpClient.get(this.siteURL + "/" + url, { responseType: 'blob'}).pipe(catchError(err => this.handleError(err)));
  }

  public updateCustomerBranding(b:CustomerBranding, file:File):Observable<any>{
    let dto=CustomerBranding.mapToDTO(b);
    let r=new UpdateBrandingRequest();
    r.customerBranding=dto;
    const formData: FormData = new FormData();
    let body=JSON.stringify(r);
    formData.append('requestString', body);
    if(file){
      let fileName = b.brandName + '.png';
      formData.append('files', file, fileName);
    }
    this.logger.log("Updating Brand file");
    return this.httpClient.post<any>(this.serviceURL + `updateBranding`, formData, this.optionsForm).pipe(catchError((error: any): any => { this.handleError(error); return of(this.createBasicResponseError(error)) }));
  }

  public deleteCustomerBranding(b:CustomerBranding){
    let r=new DeleteBrandingRequest();
    r.requestorEntityId=b.customerId;
    r.requestorEntityType="C";
    r.customerBrandingId=b.id;
    this.logger.log("Deleting Branding");
    return this.httpClient.request<any>('delete', this.serviceURL + `deleteBranding`, { body: r, headers: this.optionsJson.headers }).pipe(catchError((error: any): any => { this.handleError(error); return of(this.createBasicResponseError(error)) }));
  }
  //#endregion

  //#region customer security compliance
  public getCustomerSecurityCompliance(customerId:number){
    this.logger.log("Getting Security & Compliance");
    return this.httpClient.get<any>(this.serviceURL+ `customerSecurityCompliance/` + customerId, this.optionsJson).pipe(catchError((error: any): any=>this.handleError(error))); 
  }

  public updateCustomerSecurityCompliance(csc:CustomerSecurityCompliance):Observable<any>{
    this.logger.log("Updating Security & Compliance");
    let dto=CustomerSecurityCompliance.mapToDTO(csc);
    let r=new UpdateCustomerSecurityComplianceRequest();
    r.requestorEntityId=csc.customerId;
    r.requestorEntityType="C";
    r.customerSecurityCompliance=dto;
    return this.httpClient.put<any>(this.serviceURL + `updateCustomerSecurityCompliance`, r, this.optionsJson).pipe(catchError((error: any): any => { this.handleError(error); return of(this.createBasicResponseError(error)) }));
  }
  //#endregion

  //#region authorized sender email addresses
  public createAuthorizedSenderEmailAddress(asea:AuthorizedSenderEmailAddress):Observable<any>{
    let dto=AuthorizedSenderEmailAddress.mapToDTO(asea);
    let r=new CreateAuthorizedSenderEmailAddressRequest();
    r.authorizedSenderEmailAddress=dto;
    r.requestorEntityId=asea.customerId;
    r.requestorEntityType="C";
    return this.httpClient.post<any>(this.serviceURL+`createAuthorizedSenderEmailAddress`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  
  public updateAuthorizedSenderEmailAddress(asea:AuthorizedSenderEmailAddress){
    let dto=AuthorizedSenderEmailAddress.mapToDTO(asea);
    let r=new UpdateAuthorizedSenderEmailAddressRequest();
    r.authorizedSenderEmailAddress=dto;
    r.requestorEntityId=asea.customerId;
    r.requestorEntityType="C";
    return this.httpClient.put<any>(this.serviceURL+`updateAuthorizedSenderEmailAddress`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public enableAuthorizedSenderEmailAddress(id:number, enable:boolean){
    let r=new EnableAuthorizedSenderEmailAddressRequest();
    r.authorizedSenderEmailAddressId=id;
    r.enable=enable;
    return this.httpClient.put<any>(this.serviceURL+`enableAuthorizedSenderEmailAddress`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getAuthorizedSenderEmailAddresses(customerId:number){
    this.logger.log("Getting authorized sender email addresses");
    return this.httpClient.get<any>(this.serviceURL+`customerAuthorizedSenderEmailAddresses/` + customerId, this.optionsJson).pipe(catchError((error: any):any => this.handleError(error)));
  }

  //#endregion

  //#region customer fee schedule
  public getCustomerFeeSchedule(customerId:number, billingTypeCode:string){
    this.logger.log("Getting customer fee schedule");
    return this.httpClient.get<any>(this.serviceURL+`customerFeeSchedule/`+ customerId + '/' + billingTypeCode, this.optionsJson).pipe(catchError((error: any):any => this.handleError(error)));
  }

  public updateCustomerFeeSchedule(cfs:CustomerFeeSchedule){
    let dto=CustomerFeeSchedule.mapToDTO(cfs);
    let r=new UpdateCustomerFeeScheduleRequest();
    r.customerFeeSchedule=dto;
    r.requestorEntityId=cfs.customerId;
    r.requestorEntityType="C";
    return this.httpClient.put<any>(this.serviceURL+`updateCustomerFeeSchedule`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  //#endregion

  //#region premier partners and services
  public getPremierServiceProducts(){
    this.logger.log("Getting Premier Service Products")
    return this.httpClient.get<any>(this.serviceURL+ `premierServiceProducts`, this.optionsJson).pipe(catchError((error: any): any=>this.handleError(error))); 
  }

  public createPremierServiceProduct(psp:PremierServiceProduct):Observable<any>{
    let r = new CreatePremierServiceProductRequest();
    r.premierServiceProduct = PremierServiceProduct.mapToDTO(psp);
    return this.httpClient.post<any>(this.serviceURL+`createPremierServiceProduct`, r, this.optionsJson).pipe(catchError((error: any): any => { this.handleError(error); return of(this.createBasicResponseError(error)) }));
  }

  public updatePremierServiceProduct(psp:PremierServiceProduct){
    let dto =PremierServiceProduct.mapToDTO(psp);
    let r = new UpdatePremierServiceProductRequest();
    r.premierServiceProduct=dto;
    this.logger.log("Updating Premier Service Product");
    return this.httpClient.put<any>(this.serviceURL+`updatePremierServiceProduct`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public enablePremierServiceProduct(id:number, enable:boolean):Observable<any>{
    let r = new EnablePremierServiceProductRequest();
    r.premierServiceProductId=id;
    r.enable=enable;
    return this.httpClient.put<any>(this.serviceURL+`enablePremierServiceProduct`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getPremierServiceTypes(){
    this.logger.log("Getting Premier Service Types")
    return this.httpClient.get<any>(this.serviceURL+ `premierServiceTypes` , this.optionsJson).pipe(catchError((error: any): any=>this.handleError(error))); 
  }

  public getPremierServiceProductTypes(){
    this.logger.log("Getting Premier Service Product Types");
    return this.httpClient.get<any>(this.serviceURL+ `premierServiceProductTypes`, this.optionsJson).pipe(catchError((error: any): any=>this.handleError(error))); 
  }

  public getPremierPartners(customerId:number|undefined){
    this.logger.log("Getting Premier Partners");
    return this.httpClient.get<any>(this.serviceURL+ `premierPartners/`+ (customerId?customerId:''), this.optionsJson).pipe(catchError((error: any):any => this.handleError(error)));
  }

  public createCustomerPremierPartnerServiceConfiguration(cps:CustomerPremierPartnerServiceConfiguration){
    let r = new CreateCustomerPremierPartnerServiceConfigurationRequest();
    r.premierServiceConfiguration = CustomerPremierPartnerServiceConfiguration.mapToDTO(cps);
    r.requestorEntityId=cps.customerId;
    r.requestorEntityType="C";
    return this.httpClient.post<any>(this.serviceURL + `createCustomerPremierPartnerServiceConfiguration`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public updateCustomerPremierPartnerServiceConfiguration(cpsc:CustomerPremierPartnerServiceConfiguration){
    let r=new UpdateCustomerPremierPartnerServiceConfigurationRequest();
    r.premierServiceConfiguration=CustomerPremierPartnerServiceConfiguration.mapToDTO(cpsc);
    r.requestorEntityId=cpsc.customerId;
    r.requestorEntityType="C";
    return this.httpClient.put<any>(this.serviceURL+`updateCustomerPremierPartnerServiceConfiguration`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public deleteCustomerPremierPartnerServiceConfiguration(cpsc:CustomerPremierPartnerServiceConfiguration){
    let r=new DeleteCustomerPremierPartnerServiceConfigurationRequest();
    r.requestorEntityId=cpsc.customerId;
    r.requestorEntityType="C";
    r.premierServiceConfigurationId=cpsc.id
    this.logger.log("Deleting Customer Premier Partner Service Configuration");
    return this.httpClient.request<any>('delete', this.serviceURL + `deleteCustomerPremierPartnerServiceConfiguration`, {body:r, headers:this.optionsJson.headers}).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getCustomerPremierPartnerServiceConfigurations(customerId:number){
    return this.httpClient.get<any>(this.serviceURL+`customerPremierPartnerServiceConfigurations/` + customerId, this.optionsJson).pipe(catchError((error: any):any => this.handleError(error)));
  }
  //#endregion

  //#region qb
  public getQBAuthorizationURL(token:string, nonce:string){
    const headers = this.optionsJson.headers.set('Authorization', `Bearer ${token}`);
    const context = new HttpContext().set(SKIP_AUTH_INTERCEPTOR, true);
    return this.httpClient.get<any>(this.serviceURL+`qbAuthorizationUrl/` + encodeURIComponent(nonce), { ...this.optionsJson, headers, context }).pipe(catchError((error: any):any => this.handleError(error)));
  }

  public processQBAuthorizationCode(nonce: string, state: string, code: string, realmId: string) {
    const context = new HttpContext().set(SKIP_AUTH_INTERCEPTOR, true);
    const r = new QBProcessAuthorizationCodeRequest();
    r.code = code;
    r.realmId = realmId;
    r.nonce = nonce;
    r.state = state;
    return this.httpClient.post<any>(this.serviceURL + `qbProcessAuthorizationCode`, r, { ...this.optionsJson, context })
      .pipe(catchError((error: any): any => this.handleError(error)));
  }

  public qbIsEntityAuthorized(customerId:number, clientIds:number[]){
    let r = new QBIsEntityAuthorizedRequest();
    r.customerId=customerId;
    r.clientIds=clientIds;
    return this.httpClient.post<any>(this.serviceURL + `qbIsEntityAuthorized`, r, this.optionsJson).pipe(catchError((error: any):any => this.handleError(error)));
  }

  public qbIsEntityAuthorizedExternal(token:string){
    const headers = this.optionsJson.headers.set('Authorization', `Bearer ${token}`);
    const context = new HttpContext().set(SKIP_AUTH_INTERCEPTOR, true);
    return this.httpClient.post<any>(this.serviceURL + `qbIsEntityAuthorized`, {}, { ...this.optionsJson, headers, context }).pipe(catchError((error: any): any => this.handleError(error)));
  }

  public qbEntityDisconnected(realmId:string){
    return this.httpClient.get<any>(this.serviceURL+`qbEntityDisconnected/`+realmId, this.optionsJson).pipe(catchError((error: any):any => this.handleError(error)));
  }

  public qbDeleteEntity(customerId:number, clientId:number){
     //at the time of coding this httpClient.delete does not allow for a body. using universal request instead.
    let r = new QBEntityDeleteRequest();
    r.customerId=customerId;
    r.clientId=clientId;
    return this.httpClient.request<any>('delete', this.serviceURL+`qbDeleteEntity`, {body: r, headers: this.optionsJson.headers}).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public qbAllAccounts(customerId:number,clientId:number){
    return this.httpClient.get<any>(this.serviceURL+`qbAllAccounts/`+customerId+'/'+clientId, this.optionsJson).pipe(catchError((error: any):any => this.handleError(error)));
  }

  public qbAllClasses(customerId:number,clientId:number){
    return this.httpClient.get<any>(this.serviceURL+`qbAllClasses/`+customerId+'/'+clientId, this.optionsJson).pipe(catchError((error: any):any => this.handleError(error)));
  }

  public qbAllDepartments(customerId:number,clientId:number){
    return this.httpClient.get<any>(this.serviceURL+`qbAllDepartments/`+customerId+'/'+clientId, this.optionsJson).pipe(catchError((error: any):any => this.handleError(error)));
  }
  //#endregion

  //#region xero
  //the first 2 methods just satisfy DET-675
  public getXeroSignUpAuthorizationURL(nonce: string) {
    const context = new HttpContext().set(SKIP_AUTH_INTERCEPTOR, true);
    return this.httpClient.get<any>(this.serviceURL + `xeroSignUpAuthorizationUrl/` + encodeURIComponent(nonce), { ...this.optionsJson, context }).pipe(catchError((error: any): any => this.handleError(error)));
  }

  public processXeroSignUpAuthorizationCode(nonce: string, state: string, code: string, scope: string) {
    const context = new HttpContext().set(SKIP_AUTH_INTERCEPTOR, true);
    const r = new XeroProcessAuthorizationCodeRequest();
    r.code = code;
    r.scope = scope;
    r.nonce = nonce;
    r.state = state;
    return this.httpClient.post<any>(this.serviceURL + `xeroProcessSignUpAuthorizationCode`, r, { ...this.optionsJson, context }).pipe(catchError((error: any): any => this.handleError(error)));
  }

  public getXeroAuthorizationURL(token:string, nonce:string){
    const headers = this.optionsJson.headers.set('Authorization', `Bearer ${token}`);
    const context = new HttpContext().set(SKIP_AUTH_INTERCEPTOR, true);
    return this.httpClient.get<any>(this.serviceURL+`xeroAuthorizationUrl/` + encodeURIComponent(nonce), { ...this.optionsJson, headers, context }).pipe(catchError((error: any):any => this.handleError(error)));
  }

  public processXeroAuthorizationCode(nonce: string, state: string, code: string, scope: string) {
    const context = new HttpContext().set(SKIP_AUTH_INTERCEPTOR, true);
    const r = new XeroProcessAuthorizationCodeRequest();
    r.code = code;
    r.scope = scope;
    r.nonce = nonce;
    r.state = state;
    return this.httpClient.post<any>(this.serviceURL + `xeroProcessAuthorizationCode`, r, { ...this.optionsJson, context }).pipe(catchError((error: any): any => this.handleError(error)));
  }

  public xeroIsEntityAuthorized(customerId:number, clientIds:number[]){
    let r = new XeroIsEntityAuthorizedRequest();
    r.customerId=customerId;
    r.clientIds=clientIds;
    return this.httpClient.post<any>(this.serviceURL + `xeroIsEntityAuthorized`, r, this.optionsJson).pipe(catchError((error: any):any => this.handleError(error)));
  }

  public xeroIsEntityAuthorizedExternal(token: string) {
    const headers = this.optionsJson.headers.set('Authorization', `Bearer ${token}`);
    const context = new HttpContext().set(SKIP_AUTH_INTERCEPTOR, true);
    return this.httpClient.post<any>(this.serviceURL + `xeroIsEntityAuthorized`, {}, { ...this.optionsJson, headers, context }).pipe(catchError((error: any): any => this.handleError(error)));
  }

  public xeroEntityDisconnected(tenantId:string){
    return this.httpClient.get<any>(this.serviceURL+`xeroEntityDisconnected/`+tenantId, this.optionsJson).pipe(catchError((error: any):any => this.handleError(error)));
  }

  public xeroDeleteEntity(customerId:number, clientId:number){
    //at the time of coding this httpClient.delete does not allow for a body. using universal request instead.
   let r = new XeroEntityDeleteRequest();
   r.customerId=customerId;
   r.clientId=clientId;
   return this.httpClient.request<any>('delete', this.serviceURL+`xeroDeleteEntity`, {body: r, headers: this.optionsJson.headers}).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
 }

  public xeroAllAccounts(customerId:number,clientId:number){
    return this.httpClient.get<any>(this.serviceURL+`xeroAllAccounts/`+customerId+'/'+clientId, this.optionsJson).pipe(catchError((error: any):any => this.handleError(error)));
  }

  public xeroAllTrackingCategories(customerId:number,clientId:number){
    return this.httpClient.get<any>(this.serviceURL+`xeroAllTrackingCategories/`+customerId+'/'+clientId, this.optionsJson).pipe(catchError((error: any):any => this.handleError(error)));
  }
  //#endregion

  //#region dropbox
  public getDropboxAuthorizationURL(customerId:number){
    return this.httpClient.get<any>(this.serviceURL+`dropboxAuthorizationUrl/` + customerId, this.optionsJson).pipe(catchError((error: any):any => this.handleError(error)));
  }

  public getDropboxAuthorizationURLExternal(token:string, nonce:string){
    const headers = this.optionsJson.headers.set('Authorization', `Bearer ${token}`);
    const context = new HttpContext().set(SKIP_AUTH_INTERCEPTOR, true);
    return this.httpClient.get<any>(this.serviceURL+`dropboxAuthorizationUrlExternal/` + encodeURIComponent(nonce), { ...this.optionsJson, headers, context }).pipe(catchError((error: any):any => this.handleError(error)));
  }

  public processDropboxAuthorizationCode(nonce:string, state:string, code:string){
    let r=new DropboxProcessAuthorizationCodeRequest();
    r.code=code;
    r.nonce=nonce;
    r.state=state;
    const context = new HttpContext().set(SKIP_AUTH_INTERCEPTOR, true);
    return this.httpClient.post<any>(this.serviceURL + `dropboxProcessAuthorizationCode`, r, {...this.optionsJson, context}).pipe(catchError((error: any):any => this.handleError(error)));
  }

  public processDropboxAuthorizationCodeFileSharing(state:string, code:string){
    let r=new DropboxProcessAuthorizationCodeRequest();
    r.code=code;
    r.state=state;
    return this.httpClient.post<any>(this.serviceURL + `dropboxProcessAuthorizationCodeFileSharing`, r, this.optionsJson).pipe(catchError((error: any):any => this.handleError(error)));
  }

  public dropboxIsEntityAuthorized(requestorId:number, customerId:number, clientIds:number[]){
    let r = new DropboxIsEntityAuthorizedRequest();
    r.requestorId==requestorId;
    r.customerId=customerId;
    r.clientIds=clientIds;
    return this.httpClient.post<any>(this.serviceURL + `dropboxIsEntityAuthorized`, r, this.optionsJson).pipe(catchError((error: any):any => this.handleError(error)));
  }

  public dropboxIsEntityAuthorizedExternal(token:string){
    const headers = this.optionsJson.headers.set('Authorization', `Bearer ${token}`);
    const context = new HttpContext().set(SKIP_AUTH_INTERCEPTOR, true);
    return this.httpClient.post<any>(this.serviceURL + `dropboxIsEntityAuthorized`, {}, { ...this.optionsJson, headers, context }).pipe(catchError((error: any): any => this.handleError(error)));
  }

  public dropboxRevokeToken(requestorId:number, customerId:number, clientId:number){
    let r = new DropboxRevokeTokenRequest();
    r.requestorId==requestorId;
    r.customerId=customerId;
    r.clientId=clientId;
    return this.httpClient.post<any>(this.serviceURL + `dropboxRevokeToken`, r, this.optionsJson).pipe(catchError((error: any):any => this.handleError(error)));
  }
  //#endregion

  //#region microsoft
  public getMicrosoftAuthorizationURLExternal(token:string, nonce:string, appType?:MicrosoftAppType){
    const headers = this.optionsJson.headers.set('Authorization', `Bearer ${token}`);
    const context = new HttpContext().set(SKIP_AUTH_INTERCEPTOR, true);
    return this.httpClient.get<any>(this.serviceURL+`microsoftAuthorizationUrl/` + encodeURIComponent(nonce) + (appType ? `?appType=${encodeURIComponent(appType)}` : ''), { ...this.optionsJson, headers, context }).pipe(catchError((error: any):any => this.handleError(error)));
  }

  public processMicrosoftAdminConsentExternal(nonce: string, state: string, adminConsent: boolean, code?: string) {
    const context = new HttpContext().set(SKIP_AUTH_INTERCEPTOR, true);
    const r = new MicrosoftProcessAdminConsentRequest();
    r.adminConsent = adminConsent;
    r.code = code;
    r.nonce = nonce;
    r.state = state;
    return this.httpClient.post<any>(this.serviceURL + `microsoftProcessAdminConsent`, r, { ...this.optionsJson, context })
      .pipe(catchError((error: any): any => this.handleError(error)));
  }

  public microsoftIsEntityAuthorized(customerId:number, clientIds:number[], appType?:MicrosoftAppType){
    let r = new MicrosoftIsEntityAuthorizedRequest();
    r.customerId=customerId;
    r.clientIds=clientIds;
    r.appType=appType;
    return this.httpClient.post<any>(this.serviceURL + `microsoftIsEntityAuthorized`, r, this.optionsJson).pipe(catchError((error: any):any => this.handleError(error)));
  }

  public microsoftIsEntityAuthorizedExternal(token:string, appType?:MicrosoftAppType){
    const headers = this.optionsJson.headers.set('Authorization', `Bearer ${token}`);
    const context = new HttpContext().set(SKIP_AUTH_INTERCEPTOR, true);
    return this.httpClient.post<any>(this.serviceURL + `microsoftIsEntityAuthorized`, appType ? {appType} : {}, { ...this.optionsJson, headers, context }).pipe(catchError((error: any): any => this.handleError(error)));
  }

  public microsoftAuthorizedEntities(customerId:number, appType?:MicrosoftAppType){
    let r = new MicrosoftAuthorizedEntitiesRequest();
    r.customerId=customerId;
    r.appType=appType;
    return this.httpClient.post<any>(this.serviceURL + `microsoftAuthorizedEntities`, r, this.optionsJson).pipe(catchError((error: any):any => this.handleError(error)));
  }

  public microsoftDisconnectEntity(customerId:number, clientId:number, appType?:MicrosoftAppType){
    let r = new MicrosoftEntityDisconnectedRequest();
    r.customerId=customerId;
    r.clientId=clientId;
    r.appType=appType;
    return this.httpClient.post<any>(this.serviceURL + `microsoftDisconnectEntity`, r, this.optionsJson).pipe(catchError((error: any):any => this.handleError(error)));
  }

  public microsoftDeleteEntity(customerId:number, clientId:number, appType?:MicrosoftAppType){
    //at the time of coding this httpClient.delete does not allow for a body. using universal request instead.
    let r = new MicrosoftEntityDeleteRequest();
    r.customerId=customerId;
    r.clientId=clientId;
    r.appType=appType;
    return this.httpClient.request<any>('delete', this.serviceURL+`microsoftDeleteEntity`, {body: r, headers: this.optionsJson.headers}).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  //#endregion

  //#region prismhr
   public prismHRAPIPermissions(api_url:string, username:string, password:string, peoId:string){
    let r = new PrismHRAPIPermissionsRequest();
    r.apiUrl=api_url;
    r.username=username;
    r.password=password;
    r.peoId=peoId;
    return this.httpClient.post<any>(this.serviceURL + `prismHRAPIPermissions`, r, this.optionsJson).pipe(catchError((error: any):any => this.handleError(error)));
  }
  //#endregion

  //#region file sharing
  public getFileSharingAccounts(customerId:number){
    return this.httpClient.get<any>(this.serviceURL+`fileSharingAccounts/` + customerId + '/', this.optionsJson).pipe(catchError((error: any):any => this.handleError(error)));
  }

  public updateFileSharingAccount(fsa:FileSharingAccount){
    let dto=FileSharingAccount.mapToDTO(fsa);
    let r=new UpdateFileSharingAccountRequest();
    r.requestorEntityId=fsa.customerId;
    r.requestorEntityType="C";
    r.fileSharingAccount=dto;
    return this.httpClient.put<any>(this.serviceURL+`updateFileSharingAccount`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  public deleteFileSharingAccount(fsa:FileSharingAccount){
    let dto=FileSharingAccount.mapToDTO(fsa);
    let r=new DeleteFileSharingAccountRequest();
    r.requestorEntityId=fsa.customerId;
    r.requestorEntityType="C";
    r.fileSharingAccount=dto;
    return this.httpClient.request<any>('delete', this.serviceURL + `deleteFileSharingAccount`, {body: r, headers:this.optionsJson.headers}).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  //#endregion

  //#region billing buckets
  public createBillingBucket(b:BillingBucket):Observable<any>{
    let dto=BillingBucket.mapToDTO(b);
    let r=new CreateBillingBucketRequest();
    r.billingBucket=dto;
    return this.httpClient.post<any>(this.serviceURL+`createBillingBucket`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public updateBillingBucket(b:BillingBucket):Observable<any>{
    let dto=BillingBucket.mapToDTO(b);
    let r=new UpdateBillingBucketRequest();
    r.billingBucket=dto;
    return this.httpClient.put<any>(this.serviceURL+`updateBillingBucket`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public enableBillingBucket(id:number, enable:boolean):Observable<any>{
    let r=new EnableBillingBucketRequest();
    r.billingBucketId=id;
    r.enable=enable;
    return this.httpClient.put<any>(this.serviceURL+`enableBillingBucket`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getBillingBuckets(){
    this.logger.log("Getting billing buckets");
    return this.httpClient.get<any>(this.serviceURL+`billingBuckets`, this.optionsJson).pipe(catchError((error: any):any => this.handleError(error)));
  }
  //#endregion

  //#region wizard session
   public setWizardSession(ws:WizardSession):Observable<any>{
    this.logger.log("Setting wizard session");
    let dto=WizardSession.mapToDTO(ws);
    return this.httpClient.post<any>(this.serviceURL+`setWizardSession`, dto, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getWizardSession(){
    this.logger.log("Getting wizard session");
    return this.httpClient.get<any>(this.serviceURL+`wizardSession`, this.optionsJson).pipe(catchError((error: any):any => this.handleError(error)));
  }
  //#endregion

  //#region general stuff
  private createBasicResponseError(error: HttpErrorResponse){
    let br = new BasicResponse();
    br.success=false;
    br.message='Backend error - please try again later';
    return br;
  }

  private handleError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error.message);
    } 
    else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong
      console.error(
        `Backend returned code ${error.status}, ` +
        `body was: ${error.error}`);
    }
    // return an observable with a user-facing error message
    return throwError(()=>(error));
  }
  //#endregion
}
